import fetchData from '@/api-client/fetcher';
import { API_BASE_URL } from '@/api-client/config';
import {
  requestOrganizations,
  notDeletedWhere,
} from '@/graphql/queries/organizations/use-get-organizations';

// ---------------------------------------------------------------------------
// IAM REST — Fetch organizations from Selise Blocks identity provider
// These are REST endpoints (not graphql). GraphQL org CRUD has moved to:
//   src/graphql/mutations/organizations/
//   src/graphql/queries/organizations/
// ---------------------------------------------------------------------------

export interface IamOrganization {
  itemId: string;
  name: string;
  createdBy: string;
  isEnable: boolean;
}

export interface GetIamOrganizationsResponse {
  isSuccess?: boolean;
  organizations?: Array<{
    itemId: string;
    name?: string;
    createdBy?: string;
    isDisabled?: boolean;
  }>;
  totalCount?: number;
}

export const getIamOrganizations = async (
  filters: {
    name?: string;
    createdBy?: string;
    isEnable?: boolean;
    page?: number;
    pageSize?: number;
  } = {}
): Promise<IamOrganization[]> => {
  try {
    const params = new URLSearchParams({
      Page: String(filters.page ?? 1),
      PageSize: String(filters.pageSize ?? 10),
    });

    if (filters.name) params.set('Filter.Search', filters.name);

    const response = await fetchData<GetIamOrganizationsResponse>(
      `${API_BASE_URL}/iam/v4/iam/organizations?${params.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const orgs = response?.organizations ?? [];

    return orgs
      .filter((org) => {
        if (filters.name && org.name !== filters.name) return false;
        if (filters.createdBy && org.createdBy !== filters.createdBy) return false;
        return true;
      })
      .map((org) => ({
        itemId: org.itemId,
        name: org.name ?? '',
        createdBy: org.createdBy ?? '',
        isEnable: !org.isDisabled,
      }));
  } catch (error) {
    console.error('Error fetching IAM organizations:', error);
    return [];
  }
};

export interface MyIamOrganization {
  itemId: string;
  name: string;
}

interface GetMyIamOrganizationsResponse {
  organizations?: MyIamOrganization[];
}

export const getMyIamOrganizations = async (): Promise<MyIamOrganization[]> => {
  const response = await fetchData<
    GetMyIamOrganizationsResponse & { response?: GetMyIamOrganizationsResponse }
  >('/iam/v4/iam/organizations/my', {
    method: 'GET',
    credentials: 'include',
  });

  const organizations = response?.organizations ?? response?.response?.organizations ?? [];

  return organizations.filter((org) => Boolean(org.itemId) && org.itemId !== 'default');
};

export const checkOrgSlugAvailability = async (
  slug: string,
  excludeId?: string
): Promise<boolean> => {
  try {
    const result = await requestOrganizations(
      {
        where: {
          and: [
            { Slug: { eq: slug } },
            notDeletedWhere(),
          ],
        },
        paging: { pageNo: 1, pageSize: 5 },
      }
    );
    const items = (result?.getOrganizationss?.items ?? []).filter((item) => !item.IsDeleted);
    if (excludeId) {
      return items.every((item) => item.ItemId === excludeId);
    }
    return items.length === 0;
  } catch {
    return false;
  }
};
